import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import BackButton from '../components/BackButton';
import Footer from '../components/Footer';
import '../App.scss';

type OperationType = 'addition' | 'subtraction' | 'multiplication' | 'division';

interface Operation {
  type: OperationType;
  a: number;
  b: number;
  answer: number;
  choices: number[];
}

interface HighScores {
  addition: number;
  subtraction: number;
  multiplication: number;
  division: number;
  mixed: number;
}

const MathOperations: React.FC = () => {
  const navigate = useNavigate();
  const [score, setScore] = useState<number>(0);
  const [timeLeft, setTimeLeft] = useState<number>(5);
  const [isTimerRunning, setIsTimerRunning] = useState<boolean>(true);
  const [highScores, setHighScores] = useState<HighScores>({
    addition: 0,
    subtraction: 0,
    multiplication: 0,
    division: 0,
    mixed: 0
  });
  const [newHighScore, setNewHighScore] = useState<boolean>(false);
  const [currentOperation, setCurrentOperation] = useState<Operation | null>(null);
  const [showFeedback, setShowFeedback] = useState<boolean>(false);
  const [isCorrect, setIsCorrect] = useState<boolean | null>(null);
  const [selectedOperations, setSelectedOperations] = useState<OperationType[]>(['addition']);
  const [maxNumber, setMaxNumber] = useState<number>(10);
  const [gameOver, setGameOver] = useState<boolean>(false);
  const [highScore, setHighScore] = useState(() => {
    const savedScore = localStorage.getItem(`highScore_${selectedOperations.length > 1 ? 'mixed' : selectedOperations[0]}`);
    return savedScore ? parseInt(savedScore) : 0;
  });
  const [isNewRecord, setIsNewRecord] = useState(false);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [timerDuration, setTimerDuration] = useState<number>(5);

  // Load high scores from localStorage
  useEffect(() => {
    const savedScores = localStorage.getItem('mathHighScores');
    if (savedScores) {
      setHighScores(JSON.parse(savedScores));
    }
  }, []);

  useEffect(() => {
    // Load high score for current operation
    const savedScore = localStorage.getItem(`highScore_${selectedOperations.length > 1 ? 'mixed' : selectedOperations[0]}`);
    setHighScore(savedScore ? parseInt(savedScore) : 0);
    setIsNewRecord(false);
  }, [selectedOperations.length]);

  useEffect(() => {
    // Check and update high score
    if (score > highScore) {
      setHighScore(score);
      setIsNewRecord(true);
      localStorage.setItem(`highScore_${selectedOperations.length > 1 ? 'mixed' : selectedOperations[0]}`, score.toString());
    }
  }, [score, highScore, selectedOperations.length]);

  // Update difficulty based on score
  useEffect(() => {
    const difficultyLevel = Math.floor(score / 5);
    setMaxNumber(10 + difficultyLevel * 5); // Increase number range by 5 each level
    setTimerDuration(Math.max(2, 5 - difficultyLevel * 0.5)); // Reduce timer by 0.5s each level, min 2s
  }, [score]);

  // Timer effect
  useEffect(() => {
    if (!isTimerRunning || showFeedback || gameOver) return;

    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 0) {
          clearInterval(timer);
          handleTimeout();
          return 0;
        }
        return prev - 0.1;
      });
    }, 100);

    return () => clearInterval(timer);
  }, [isTimerRunning, showFeedback, gameOver]);

  // Reset timer when new operation is generated
  useEffect(() => {
    if (currentOperation) {
      setTimeLeft(timerDuration);
      setIsTimerRunning(true);
    }
  }, [currentOperation, timerDuration]);

  const getCurrentScoreType = (): keyof HighScores => {
    if (selectedOperations.length === 1) {
      return selectedOperations[0];
    }
    return 'mixed';
  };

  const updateHighScore = (newScore: number) => {
    const scoreType = getCurrentScoreType();
    if (newScore > highScores[scoreType]) {
      const newHighScores = { ...highScores, [scoreType]: newScore };
      setHighScores(newHighScores);
      localStorage.setItem('mathHighScores', JSON.stringify(newHighScores));
      setNewHighScore(true);
      setTimeout(() => setNewHighScore(false), 3000); // Reset animation after 3 seconds
    }
  };

  const generateChoices = (correctAnswer: number): number[] => {
    const choices = [correctAnswer]; // Always include the correct answer
    const maxAttempts = 20; // Prevent infinite loop
    let attempts = 0;

    while (choices.length < 4 && attempts < maxAttempts) {
      attempts++;
      // Generate a random offset between -5 and +5, but not 0
      let offset = Math.floor(Math.random() * 10) - 5;
      if (offset === 0) offset = 1;
      
      const newChoice = correctAnswer + offset;
      if (!choices.includes(newChoice) && newChoice > 0) {
        choices.push(newChoice);
      }
    }

    // If we couldn't generate enough unique choices, fill with sequential numbers
    while (choices.length < 4) {
      const newChoice = correctAnswer + choices.length;
      if (!choices.includes(newChoice) && newChoice > 0) {
        choices.push(newChoice);
      }
    }

    // Shuffle the choices
    return choices.sort(() => Math.random() - 0.5);
  };

  const generateOperation = (): Operation => {
    let type: OperationType;
    if (selectedOperations.length === 4) {
      // For mixed operations, randomly select one
      type = selectedOperations[Math.floor(Math.random() * selectedOperations.length)];
    } else {
      // For single operation, use the selected one
      type = selectedOperations[0];
    }
    
    let a, b, answer;

    switch (type) {
      case 'addition':
        a = Math.floor(Math.random() * maxNumber) + 1;
        b = Math.floor(Math.random() * maxNumber) + 1;
        answer = a + b;
        break;
      case 'subtraction':
        a = Math.floor(Math.random() * maxNumber) + 1;
        b = Math.floor(Math.random() * a) + 1;
        answer = a - b;
        break;
      case 'multiplication':
        a = Math.floor(Math.random() * maxNumber) + 1;
        b = Math.floor(Math.random() * maxNumber) + 1;
        answer = a * b;
        break;
      case 'division':
        answer = Math.floor(Math.random() * maxNumber) + 1;
        b = Math.floor(Math.random() * maxNumber) + 1;
        a = answer * b;
        break;
    }

    const choices = generateChoices(answer);
    return { type, a, b, answer, choices };
  };

  const getOperationSymbol = (type: OperationType): string => {
    switch (type) {
      case 'addition': return '+';
      case 'subtraction': return '-';
      case 'multiplication': return '×';
      case 'division': return '÷';
    }
  };

  const handleTimeout = () => {
    if (!currentOperation || showFeedback || gameOver) return;
    
    setIsTimerRunning(false);
    setSelectedAnswer(null);
    setShowFeedback(true);
    setIsCorrect(false);
    setGameOver(true);
  };

  const handleAnswerClick = (choice: number) => {
    if (!currentOperation || showFeedback || gameOver) return;

    setIsTimerRunning(false);
    setSelectedAnswer(choice);
    const isCorrectAnswer = choice === currentOperation.answer;

    if (isCorrectAnswer) {
      const newScore = score + 1;
      setScore(newScore);
      updateHighScore(newScore);
      setIsCorrect(true);
      setShowFeedback(true);
      // Auto advance to next question after 1.5 seconds
      setTimeout(() => {
        handleNextOperation();
        setSelectedAnswer(null);
        setShowFeedback(false);
        setIsCorrect(null);
      }, 1500);
    } else {
      setGameOver(true);
      setIsCorrect(false);
      setShowFeedback(true);
    }
  };

  const handleNextOperation = () => {
    setCurrentOperation(generateOperation());
    setShowFeedback(false);
    setIsCorrect(null);
    setSelectedAnswer(null);
    setTimeLeft(timerDuration);
    setIsTimerRunning(true);
  };

  const startNewGame = () => {
    setScore(0);
    setGameOver(false);
    setShowFeedback(false);
    setIsCorrect(null);
    setSelectedAnswer(null);
    setIsNewRecord(false);
    setMaxNumber(10); // Reset difficulty
    setTimerDuration(5); // Reset timer duration
    setTimeLeft(5);
    setIsTimerRunning(true);
    setCurrentOperation(generateOperation());
  };

  const toggleOperation = (operation: OperationType | 'mixed') => {
    if (operation === 'mixed') {
      setSelectedOperations(['addition', 'subtraction', 'multiplication', 'division']);
    } else {
      setSelectedOperations([operation]);
    }
    // Reset game state and generate new operation
    setScore(0);
    setGameOver(false);
    setShowFeedback(false);
    setIsCorrect(null);
    setSelectedAnswer(null);
    setIsNewRecord(false);
    
    // Generate a new operation immediately
    const newOperation = operation === 'mixed' ? 
      generateOperation() : 
      { ...generateOperation(), type: operation as OperationType };
    setCurrentOperation(newOperation);
  };

  useEffect(() => {
    handleNextOperation();
  }, []);

  return (
    <div className="math-operations-game">
      <div className="game-header">
        <BackButton />
      </div>
      <h1>Math Operations</h1>

      <div className="operation-types">
        <h3>Select Operation to Practice:</h3>
        <div className="operation-buttons">
          <button
            className={selectedOperations.length === 4 ? 'active' : ''}
            onClick={() => !gameOver && toggleOperation('mixed')}
            disabled={gameOver}
          >
            All
          </button>
          <button
            className={selectedOperations.length === 1 && selectedOperations[0] === 'addition' ? 'active' : ''}
            onClick={() => !gameOver && toggleOperation('addition')}
            disabled={gameOver}
          >
            +
          </button>
          <button
            className={selectedOperations.length === 1 && selectedOperations[0] === 'subtraction' ? 'active' : ''}
            onClick={() => !gameOver && toggleOperation('subtraction')}
            disabled={gameOver}
          >
            -
          </button>
          <button
            className={selectedOperations.length === 1 && selectedOperations[0] === 'multiplication' ? 'active' : ''}
            onClick={() => !gameOver && toggleOperation('multiplication')}
            disabled={gameOver}
          >
            ×
          </button>
          <button
            className={selectedOperations.length === 1 && selectedOperations[0] === 'division' ? 'active' : ''}
            onClick={() => !gameOver && toggleOperation('division')}
            disabled={gameOver}
          >
            ÷
          </button>
        </div>
      </div>

      <div className="game-score">
   
        <div className={`high-score ${isNewRecord ? 'new-record' : ''}`}>
        <b>Score: {score}</b> |     <span className="best-score">Best: {highScore}</span>
          {isNewRecord && <span className="new-record-text">New Record!</span>}
        </div>
      </div>

      {currentOperation && (
        <div className="game-container">
          <div className="operation-container">
            <div 
              className={`timer-bar ${
                timeLeft <= timerDuration * 0.3 ? 'critical-time' : 
                timeLeft <= timerDuration * 0.5 ? 'low-time' : ''
              }`} 
              style={{ transform: `scaleX(${timeLeft / timerDuration})` }} 
            />
            {showFeedback && !isCorrect && (
              <h3>
                Try Again!
                <button className="retry-button" onClick={startNewGame}>
                  PLAY AGAIN
                </button>
              </h3>
            )}
            <div className="operation-display">
              {currentOperation.a} {getOperationSymbol(currentOperation.type)} {currentOperation.b} = ?
            </div>
            <div className="answer-choices">
              {currentOperation.choices.map((choice, index) => (
                <button
                  key={index}
                  onClick={() => handleAnswerClick(choice)}
                  disabled={showFeedback}
                  className={`answer-button ${
                    showFeedback && isCorrect && choice === currentOperation.answer
                      ? 'correct-unit'
                      : showFeedback && !isCorrect && choice === selectedAnswer
                      ? 'incorrect-unit'
                      : showFeedback && !isCorrect && choice === currentOperation.answer
                      ? 'correct-unit'
                      : ''
                  }`}
                >
                  {choice}
                  {showFeedback && choice === selectedAnswer && (
                    <div className={`tooltip ${isCorrect ? '' : 'wrong'}`}>
                      {isCorrect ? 'Correct!' : 'Wrong!'}
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="game-instructions">
        <h3>How to Play:</h3>
        <p>Select which operations you want to practice using the buttons above.</p>
        <p>Choose the correct answer from the options provided.</p>
        <p>Keep playing as long as you get the answers right!</p>
        <p>The game ends if you choose an incorrect answer or if you fail to answer within 5 seconds!</p>
      </div>

      <div className="educational-benefits">
        <h2>Benefits of Math Operations Practice</h2>
        <div className="benefits-content">
          <div className="benefit-section">
            <h3>Developing Mental Math Skills</h3>
            <p>Regular practice with basic math operations helps children develop quick mental math abilities, a crucial skill for both academic success and daily life. Through our interactive game format, students learn to solve problems efficiently while having fun.</p>
          </div>
          
          <div className="benefit-section">
            <h3>Building Confidence</h3>
            <p>As children progress and see their scores improve, they build confidence in their mathematical abilities. The immediate feedback and visual rewards system encourages continued practice and creates a positive association with mathematics.</p>
          </div>
          
          <div className="benefit-section">
            <h3>Adaptive Learning</h3>
            <p>Our game adapts to each child's level by allowing them to choose which operations to practice. This customization ensures that learning remains challenging yet achievable, maintaining engagement and promoting steady progress.</p>
          </div>
          
          <div className="benefit-section">
            <h3>Real-World Application</h3>
            <p>The skills practiced in this game directly translate to real-world scenarios, from calculating prices at the store to solving more complex mathematical problems in higher education. Regular practice helps children understand the practical importance of mathematics.</p>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default MathOperations; 